import React, { useEffect, useRef, useState } from "react";
import {
  SESSION_EXPIRED_EVENT,
  type SessionExpiredDetail,
  type SessionExpiredReason,
} from "../utils/sessionEvents";
import { SessionExpiredModal } from "./SessionExpiredModal";

const TOAST_DEBOUNCE_MS = 3_000;

/**
 * SessionGuard wraps the app and listens for `session-expired` window events.
 * When one fires it shows the SessionExpiredModal overlay, blocking the UI
 * until the user clicks "Sign In".
 *
 * Place this as high as possible in the tree (e.g. in main.tsx) so it is
 * always mounted regardless of auth state.
 */
export const SessionGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sessionGone, setSessionGone] = useState(false);
  const [modalReason, setModalReason] = useState<SessionExpiredReason>("expired");
  const lastEventAtRef = useRef<number>(0);

  useEffect(() => {
    if (sessionGone) return; // already showing modal — ignore duplicate events

    const handleSessionExpired = (event: Event): void => {
      const reason =
        (event as CustomEvent<SessionExpiredDetail>).detail?.reason ?? "expired";

      console.warn(
        "[AUTH][datasurfr][SessionGuard.tsx] session-expired received | reason:",
        reason,
        "| time:",
        new Date().toISOString()
      );

      // Debounce: swallow events fired within 3 seconds of the first one
      const now = Date.now();
      if (now - lastEventAtRef.current < TOAST_DEBOUNCE_MS) {
        console.warn("[AUTH][datasurfr][SessionGuard.tsx] Event debounced — skipping duplicate");
        return;
      }
      lastEventAtRef.current = now;

      setModalReason(reason);
      setSessionGone(true);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [sessionGone]);

  return (
    <>
      {children}
      {sessionGone && <SessionExpiredModal reason={modalReason} />}
    </>
  );
};
